import { Button } from "@nimit9/signet-ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@nimit9/signet-ui/components/dialog"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { LearningUnreadableAlert } from "@/components/common/learning-unreadable-alert"
import { flattenCategories, useCategories } from "@/hooks/use-categories"
import { useLearning } from "@/hooks/use-learning"
import { usePersons } from "@/hooks/use-persons"
import { useUncategorizedTransactions } from "@/hooks/use-uncategorized-transactions"
import { api } from "@/lib/api"
import {
  type AutoCategorizePlan,
  autoCategorizeFromDescriptions,
} from "@/lib/categorization-client"
import { buildResolveContextExtras } from "@/lib/propagate-entity"
import { salaryPlan } from "@/lib/salary-plan"

interface MerchantsResponse {
  success: boolean
  data: Array<{
    id: string
    rawId: string | null
    cleanName: string | null
    isPerson: boolean | null
    householdId?: string | null
  }>
}

export function AutoCategorizeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const { data: catData } = useCategories()
  const { data: learningData } = useLearning()
  const { data: personsResp } = usePersons()
  const { data: merchantsResp } = useQuery<MerchantsResponse>({
    queryKey: ["merchants"],
    queryFn: () => api.get<MerchantsResponse>("/merchants"),
    enabled: open,
  })

  // Shared paged fetch — ALL uncategorized rows via cursor-loop.
  const { rows: uncategorizedRows, isLoading: isLoadingTxns } = useUncategorizedTransactions({
    enabled: open,
  })

  const allCategories = useMemo(
    () => (catData?.data ? flattenCategories(catData.data) : []),
    [catData],
  )

  const ctxExtras = useMemo(
    () =>
      buildResolveContextExtras(merchantsResp?.data ?? [], personsResp?.data ?? [], allCategories),
    [merchantsResp, personsResp, allCategories],
  )

  const merchantNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const row of merchantsResp?.data ?? []) {
      if (row.cleanName) m.set(row.id, row.cleanName)
    }
    return m
  }, [merchantsResp])
  const personNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of personsResp?.data ?? []) m.set(p.id, p.name)
    return m
  }, [personsResp])
  const categoryNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of allCategories) m.set(c.id, c.name)
    return m
  }, [allCategories])

  const plan: AutoCategorizePlan | null = useMemo(() => {
    if (!learningData || uncategorizedRows.length === 0) return null
    return autoCategorizeFromDescriptions({
      rows: uncategorizedRows.map((t) => ({
        id: t.id,
        description: t.description,
        merchantId: t.merchant?.id ?? null,
        personId: t.personId ?? null,
        categoryId: t.category?.id ?? null,
        isTransfer: t.isTransfer,
        isIgnored: t.isIgnored,
      })),
      bag: learningData.bag,
      ...ctxExtras,
    })
  }, [learningData, uncategorizedRows, ctxExtras])

  // Salary credits have no merchant/person, so the plan above never sees them
  // (same on-device rule as MCP rescan, UAT J4-01).
  const salary = useMemo(
    () => salaryPlan(uncategorizedRows, allCategories),
    [allCategories, uncategorizedRows],
  )

  const [excluded, setExcluded] = useState<Set<string>>(new Set())
  const [appliedCount, setAppliedCount] = useState<number | null>(null)

  function toggleEntity(key: string) {
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const applyMutation = useMutation({
    mutationFn: async () => {
      let salaryCount = 0
      if (salary && !excluded.has("salary")) {
        for (let i = 0; i < salary.ids.length; i += 200) {
          await api.patch("/transactions/bulk", {
            ids: salary.ids.slice(i, i + 200),
            categoryId: salary.category.id,
          })
        }
        salaryCount = salary.ids.length
      }
      if (!plan) return { linked: salaryCount }
      const links: Array<
        | { transactionId: string; merchantId: string; categoryId?: string }
        | { transactionId: string; personId: string; categoryId?: string }
      > = []
      for (const s of plan.suggestions) {
        const key =
          s.entity.type === "merchant" ? `m:${s.entity.merchantId}` : `p:${s.entity.personId}`
        if (excluded.has(key)) continue
        // Only build links that include an entity assignment OR a category
        // change. The bulk endpoint requires exactly one of merchant/person.
        for (const txId of s.transactionIds) {
          const base =
            s.entity.type === "merchant"
              ? { transactionId: txId, merchantId: s.entity.merchantId }
              : { transactionId: txId, personId: s.entity.personId }
          if (s.categoryId) links.push({ ...base, categoryId: s.categoryId })
          else links.push(base)
        }
      }
      if (links.length === 0) return { linked: salaryCount }
      const res = await api.post<{ success: boolean; linked: number }>(
        "/transactions/bulk-link-entity",
        { links },
      )
      return { linked: res.linked + salaryCount }
    },
    onSuccess: (res) => {
      setAppliedCount(res.linked)
      queryClient.invalidateQueries({ queryKey: ["transactions"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard"] })
    },
  })

  function reset() {
    setExcluded(new Set())
    setAppliedCount(null)
    applyMutation.reset()
  }

  function handleClose() {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Auto-categorize</DialogTitle>
          <DialogDescription>
            Paisa recognises these merchants and suggests a category for each. Untick any you want
            to sort yourself.
          </DialogDescription>
        </DialogHeader>

        {learningData?.readable === false && <LearningUnreadableAlert />}

        {appliedCount !== null ? (
          <div className="space-y-3 py-6 text-center">
            <p className="text-[15px] text-text-primary">
              Linked <span className="font-medium">{appliedCount}</span> transaction
              {appliedCount === 1 ? "" : "s"}.
            </p>
            <p className="text-[12px] text-text-muted">
              Open the Transactions list to review the changes.
            </p>
          </div>
        ) : isLoadingTxns || !plan ? (
          <div className="py-8 text-center text-[13px] text-text-muted">Loading…</div>
        ) : plan.suggestions.length === 0 && !salary ? (
          <div className="space-y-2 py-6 text-center">
            <p className="text-[14px] text-text-primary">Nothing to apply yet.</p>
            <p className="text-[12px] text-text-muted">
              Categorize a few transactions or assign a merchant/person on uncategorized rows to
              teach Paisa, then come back here.
            </p>
            {plan.unresolvedCount > 0 ? (
              <p className="text-[11px] text-text-hint">
                {plan.unresolvedCount} uncategorized transaction
                {plan.unresolvedCount === 1 ? "" : "s"} couldn't be matched.
              </p>
            ) : null}
          </div>
        ) : (
          <>
            <div className="text-[12px] text-text-muted space-y-0.5">
              {(() => {
                const categorized = plan.suggestions
                  .filter((s) => s.categoryId !== null)
                  .reduce((n, s) => n + s.transactionIds.length, 0)
                const linkedOnly = plan.totalAffected - categorized
                return (
                  <>
                    <p>
                      Found matches for{" "}
                      <span className="text-text-secondary font-medium">{plan.totalAffected}</span>{" "}
                      of {plan.totalAffected + plan.unresolvedCount} uncategorized transactions.
                    </p>
                    <p>
                      {categorized > 0 && (
                        <>
                          <span className="text-text-secondary font-medium">{categorized}</span>{" "}
                          will be categorized
                          {linkedOnly > 0 ? ", " : ""}
                        </>
                      )}
                      {linkedOnly > 0 && (
                        <>
                          <span className="text-text-secondary font-medium">{linkedOnly}</span>{" "}
                          recognised, pick a category once and Paisa remembers
                        </>
                      )}
                      {plan.unresolvedCount > 0 && (
                        <>
                          ,{" "}
                          <span className="text-text-secondary font-medium">
                            {plan.unresolvedCount}
                          </span>{" "}
                          still unmatched
                        </>
                      )}
                      .
                    </p>
                  </>
                )
              })()}
            </div>
            <div className="max-h-80 overflow-y-auto rounded-md border border-border-default">
              <table className="w-full text-[12px]">
                <thead className="bg-bg-elevated text-text-muted">
                  <tr>
                    <th className="px-3 py-2 text-left font-normal w-8" />
                    <th className="px-3 py-2 text-left font-normal">Linked to</th>
                    <th className="px-3 py-2 text-left font-normal">Type</th>
                    <th className="px-3 py-2 text-right font-normal">Count</th>
                    <th className="px-3 py-2 text-left font-normal">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {salary && (
                    <tr
                      className={`border-t border-border-subtle ${excluded.has("salary") ? "opacity-40" : ""}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label="Categorise salary credits"
                          checked={!excluded.has("salary")}
                          onChange={() => toggleEntity("salary")}
                        />
                      </td>
                      <td className="px-3 py-2 text-text-primary">Salary credits</td>
                      <td className="px-3 py-2 text-text-secondary">Narration</td>
                      <td className="px-3 py-2 text-right tabular-nums">{salary.ids.length}</td>
                      <td className="px-3 py-2 text-text-secondary">{salary.category.name}</td>
                    </tr>
                  )}
                  {plan.suggestions.map((s) => {
                    const key =
                      s.entity.type === "merchant"
                        ? `m:${s.entity.merchantId}`
                        : `p:${s.entity.personId}`
                    const name =
                      s.entity.type === "merchant"
                        ? (merchantNameById.get(s.entity.merchantId) ?? "Merchant")
                        : (personNameById.get(s.entity.personId) ?? "Person")
                    const catName = s.categoryId
                      ? (categoryNameById.get(s.categoryId) ?? "—")
                      : "— (link only)"
                    const isExcluded = excluded.has(key)
                    return (
                      <tr
                        key={key}
                        className={`border-t border-border-subtle ${isExcluded ? "opacity-40" : ""}`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={!isExcluded}
                            onChange={() => toggleEntity(key)}
                          />
                        </td>
                        <td className="px-3 py-2 text-text-primary">{name}</td>
                        <td className="px-3 py-2 text-text-secondary">
                          {s.entity.type === "merchant" ? "Merchant" : "Person"}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {s.transactionIds.length}
                        </td>
                        <td className="px-3 py-2 text-text-secondary">{catName}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DialogFooter>
          {appliedCount !== null ? (
            <Button onClick={handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={() => applyMutation.mutate()}
                disabled={
                  applyMutation.isPending ||
                  !plan ||
                  ((!salary || excluded.has("salary")) &&
                    plan.suggestions.every((s) => {
                      const key =
                        s.entity.type === "merchant"
                          ? `m:${s.entity.merchantId}`
                          : `p:${s.entity.personId}`
                      return excluded.has(key)
                    }))
                }
              >
                {applyMutation.isPending ? "Applying…" : "Apply"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
